/**
 * eventLogger.js
 * ─────────────────────────────────────────────────────────────────────
 * Append-only client event logger for Admin Analytics (Phase 3A).
 *
 * Buffers events in memory and flushes to Firestore `events/{auto_id}`
 * in batches to avoid one-write-per-interaction load. Each event carries
 * a stable shape so the Admin dashboard can build funnels / retention /
 * feature reports without guessing.
 *
 * IMPORTANT:
 *   - Fire-and-forget: tracking MUST NEVER throw into app flow.
 *   - PII: we never log email / name / free-text — only UID + event
 *     metadata described in EVENT_PROPERTIES.md.
 *   - Rate-safe: batch flush every 5s OR on 20 events OR on pagehide.
 */

import { db, auth } from '../firebase';
import { collection, addDoc, serverTimestamp, writeBatch, doc } from 'firebase/firestore';

// Version pulled from package.json at build time (CRA injects REACT_APP_* env).
const APP_VERSION =
  (typeof process !== 'undefined' && process.env && process.env.REACT_APP_VERSION) || 'dev';

// Canonical event names — keep this list as the single source of truth.
// When you add an event, add it here AND update docs.
export const EVENTS = Object.freeze({
  // Auth / onboarding
  SIGN_UP_COMPLETED: 'sign_up_completed',
  LOGIN_COMPLETED: 'login_completed',
  ONBOARDING_COMPLETED: 'onboarding_completed',
  LOGOUT: 'logout',

  // Portfolio / transaction
  PORTFOLIO_CREATED: 'portfolio_created',
  PORTFOLIO_DELETED: 'portfolio_deleted',
  TRANSACTION_ADDED: 'transaction_added',
  TRANSACTION_EDITED: 'transaction_edited',
  TRANSACTION_DELETED: 'transaction_deleted',
  FIRST_TRANSACTION_ADDED: 'first_transaction_added',
  HOLDING_ADDED: 'holding_added',

  // Usage
  DASHBOARD_VIEWED: 'dashboard_viewed',
  TRANSACTIONS_VIEWED: 'transactions_viewed',
  SIMULATOR_OPENED: 'simulator_opened',
  SIMULATOR_RUN: 'simulator_run',
  ANALYTICS_OPENED: 'analytics_opened',
  TAX_REPORT_OPENED: 'tax_report_opened',
  EXPORT_CSV_CLICKED: 'export_csv_clicked',
  MARKETS_OPENED: 'markets_opened',

  // Alerts
  ALERT_CREATED: 'alert_created',
  ALERT_EDITED: 'alert_edited',
  ALERT_DELETED: 'alert_deleted',
  ALERT_TRIGGERED: 'alert_triggered',
  ALERT_DELIVERY_SUCCESS: 'alert_delivery_success',
  ALERT_DELIVERY_FAILED: 'alert_delivery_failed',

  // Revenue readiness (placeholders — safe to fire now)
  PAYWALL_VIEWED: 'paywall_viewed',
  UPGRADE_CLICKED: 'upgrade_clicked',
  TRIAL_STARTED: 'trial_started',
  SUBSCRIPTION_STARTED: 'subscription_started',
  SUBSCRIPTION_RENEWED: 'subscription_renewed',
  SUBSCRIPTION_CANCELED: 'subscription_canceled',
  REFUND_RECEIVED: 'refund_received',

  // Lightweight error monitoring (logged to the events stream — no external SaaS)
  APP_ERROR: 'app_error',
});

// Buffer config
const FLUSH_INTERVAL_MS = 5_000;
const MAX_BUFFER = 20;
const MAX_BUFFER_HARD = 50; // absolute ceiling to protect memory

let buffer = [];
let flushTimer = null;
let flushing = false;

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flush();
  }, FLUSH_INTERVAL_MS);
}

/**
 * Flush buffered events to Firestore. Safe to call anytime.
 * Never throws — all errors swallowed (tracking must not break app).
 */
export async function flush() {
  if (flushing || buffer.length === 0) return;
  if (!auth.currentUser) return; // only authenticated users can append
  flushing = true;
  const toSend = buffer.splice(0, buffer.length);
  try {
    const batch = writeBatch(db);
    const colRef = collection(db, 'events');
    toSend.forEach((evt) => {
      const ref = doc(colRef);
      batch.set(ref, evt);
    });
    await batch.commit();
  } catch (err) {
    // Silent drop — do not retry aggressively (budget-safe)
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn('[eventLogger] flush failed', err?.code || err);
    }
  } finally {
    flushing = false;
  }
}

/**
 * logEvent(eventName, properties)
 *  - eventName: string  (use EVENTS.* constant when possible)
 *  - properties: shallow object of allowed metadata (no PII)
 *
 * Adds a well-formed event to the flush buffer. Safe to call often.
 */
export function logEvent(eventName, properties = {}) {
  try {
    if (!eventName || typeof eventName !== 'string') return;
    const user = auth.currentUser;
    if (!user) return; // Firestore rules require auth; skip cleanly for logged-out

    // Cap buffer to protect memory if the network is down.
    if (buffer.length >= MAX_BUFFER_HARD) {
      buffer.splice(0, buffer.length - MAX_BUFFER_HARD + 1);
    }

    buffer.push({
      event: eventName,
      user_id: user.uid,
      platform: 'web',
      app_version: APP_VERSION,
      timestamp: serverTimestamp(),
      client_ts: Date.now(),
      // caller-provided props (already sanitized by convention)
      ...sanitizeProps(properties),
    });

    if (buffer.length >= MAX_BUFFER) {
      flush();
    } else {
      scheduleFlush();
    }
  } catch (err) {
    // Silent — never break the app for tracking
  }
}

// Strip obviously sensitive / oversized fields, keep short scalars & short strings.
function sanitizeProps(obj) {
  if (!obj || typeof obj !== 'object') return {};
  const out = {};
  const BANNED = new Set(['email', 'name', 'password', 'token', 'idToken', 'phone']);
  for (const [k, v] of Object.entries(obj)) {
    if (BANNED.has(k)) continue;
    if (v === null || v === undefined) continue;
    const t = typeof v;
    if (t === 'string') {
      out[k] = v.length > 120 ? v.slice(0, 120) : v;
    } else if (t === 'number' && Number.isFinite(v)) {
      out[k] = v;
    } else if (t === 'boolean') {
      out[k] = v;
    }
    // skip objects/arrays/functions on purpose — keep events flat & small
  }
  return out;
}

// ─────────────────────────────────────────────────────────────────────
// Lightweight error monitoring — capture uncaught errors / promise
// rejections and log them as `app_error` events. Throttled + deduped so a
// runaway error can never spam Firestore (max 10/session, unique messages).
// ─────────────────────────────────────────────────────────────────────
const _seenErrors = new Set();
let _errorCount = 0;
const MAX_ERRORS_PER_SESSION = 10;

export function captureError(message, source = '', extra = {}) {
  try {
    if (_errorCount >= MAX_ERRORS_PER_SESSION) return;
    const msg = String(message || 'unknown').slice(0, 120);
    const dedupeKey = `${msg}|${source}`;
    if (_seenErrors.has(dedupeKey)) return; // already reported this one
    _seenErrors.add(dedupeKey);
    _errorCount++;
    logEvent(EVENTS.APP_ERROR, {
      message: msg,
      source: String(source || '').slice(0, 120),
      path: (typeof window !== 'undefined' ? window.location?.pathname : '') || '',
      ...extra,
    });
  } catch { /* never let error logging throw */ }
}

// Flush on pagehide (best-effort) so near-exit events aren't lost.
if (typeof window !== 'undefined') {
  const kick = () => flush();
  window.addEventListener('pagehide', kick);
  window.addEventListener('beforeunload', kick);

  // Global error hooks — fire-and-forget, never block the app.
  window.addEventListener('error', (e) => {
    captureError(e?.message || 'window.error', e?.filename ? `${e.filename}:${e.lineno || ''}` : 'window');
  });
  window.addEventListener('unhandledrejection', (e) => {
    const reason = e?.reason;
    const msg = (reason && (reason.message || reason.toString?.())) || 'unhandledrejection';
    captureError(msg, 'promise');
  });
}

// Named export for tests / manual debugging
export const __internals = {
  getBufferLength: () => buffer.length,
  clearBuffer: () => { buffer = []; },
};
